"use client";

import { Box } from "@chakra-ui/react";
import { useMemo } from "react";
import { PageTypeGlobal } from "@/lib/types";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Button, NavLink, Text } from "@/components/ui";
import {
  buildClassesPageQuery,
  EMPTY_CLASSES_PAGE_URL_STATE,
} from "@/views/classes/hooks/classesPageUrl";

export interface LocationClassesSectionProps {
  locationName: string;
  isSingleLocationStudio: boolean;
}

export const LocationClassesSection = ({
  locationName,
  isSingleLocationStudio,
}: LocationClassesSectionProps) => {
  const theme = useThemeTokens();

  const classesHref = useMemo(() => {
    if (isSingleLocationStudio) {
      return PageTypeGlobal.Classes;
    }

    return `${PageTypeGlobal.Classes}${buildClassesPageQuery({
      ...EMPTY_CLASSES_PAGE_URL_STATE,
      location: locationName,
    })}`;
  }, [isSingleLocationStudio, locationName]);

  const description = isSingleLocationStudio
    ? "Browse the full class schedule."
    : `Browse the full schedule for classes held at ${locationName}.`;

  const buttonLabel = isSingleLocationStudio
    ? "Browse all classes"
    : "View classes at this location";

  return (
    <Box py={{ base: 5, md: 6 }} borderBottomWidth="1px" borderColor={theme.borderGlass.subtle}>
      <Text
        fontSize="xs"
        fontWeight="600"
        letterSpacing="0.14em"
        textTransform="uppercase"
        color={theme.text.tertiary}
        mb={1}
      >
        Classes
      </Text>
      <Text variant="secondary" fontSize="sm" mb={4} lineHeight="1.5">
        {description}
      </Text>

      <Button
        as={NavLink}
        href={classesHref}
        variant="primary"
        size="sm"
        fontSize="sm"
        px={4}
        borderRadius="8px !important"
      >
        {buttonLabel}
      </Button>
    </Box>
  );
};
